import type { ReactNode } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { numberFormatter } from "@common/helpers/number";
import { styleWithVars } from "@lib/cssVars";
import type {
    DensityChartDay,
    DensityChartPeriod,
    DensityChartPeriodLabel,
    DensityChartSeries,
} from "./DensityChart.lib";
import type { DensityChartBucket } from "./densityBuckets.lib";
import { bucketSeriesTotal, bucketToPeriod } from "./densityBuckets.lib";
import styles from "./DensityChart.module.scss";
import { placeSliceTooltip } from "./sliceTooltipPlacement.lib";
import type { HoveredSlice } from "./useSliceHover";

/** The card's `min-width`, and its width before it is measured. */
const TOOLTIP_MIN_WIDTH_PX = 200;

interface DensityChartSliceTooltipProps {
    /** Names what the card describes, e.g. "Alpha, Oct 4". */
    ariaLabel: string;
    bucketIndex: number;
    bucketCount: number;
    plotWidth: number;
    /** The part of the plot in view, in px from its left. */
    visible: { start: number; end: number };
    /** The chart root, outside the panned strip, so the card stacks above everything in the chart. */
    container: HTMLElement | null;
    children: ReactNode;
}

/** The card beside a hovered bar; the pointer passes through it to the bars. */
function DensityChartSliceTooltip({
    ariaLabel,
    bucketIndex,
    bucketCount,
    plotWidth,
    visible,
    container,
    children,
}: DensityChartSliceTooltipProps) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [cardWidth, setCardWidth] = useState(TOOLTIP_MIN_WIDTH_PX);
    useLayoutEffect(() => {
        const card = cardRef.current;
        if (!card) {
            return;
        }
        const measure = () => {
            const { width } = card.getBoundingClientRect();
            // Hidden (display: none) during a window drag; the observer measures it once shown.
            if (width > 0) {
                setCardWidth(width);
            }
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(card);
        return () => observer.disconnect();
    }, [bucketIndex, ariaLabel]);
    const left = placeSliceTooltip({
        bucketIndex,
        bucketCount,
        plotWidth,
        tooltipWidth: cardWidth,
        visible,
    });
    if (!container) {
        return null;
    }
    return createPortal(
        <div
            ref={cardRef}
            className={styles.sliceTooltip}
            style={styleWithVars({
                "--tooltip-left": `${left - visible.start}px`,
                "--tooltip-min-width": `${TOOLTIP_MIN_WIDTH_PX}px`,
            })}
            role="group"
            aria-label={ariaLabel}
        >
            {children}
        </div>,
        container,
    );
}

interface DensityChartSeriesSliceProps {
    series: DensityChartSeries;
    periodLabel: string;
    total: number;
}

/** A segment's card content on one line: its period, its series and its count. */
function DensityChartSeriesSlice({
    series,
    periodLabel,
    total,
}: DensityChartSeriesSliceProps) {
    return (
        <div className={styles.sliceRow}>
            <span className={styles.slicePeriod}>{periodLabel}</span>
            <span className={styles.sliceSeries}>
                {series.icon ?? (
                    <span
                        className={styles.sliceDot}
                        style={{
                            background: series.accentColor ?? series.color,
                        }}
                        aria-hidden
                    />
                )}
                <span className={styles.sliceLabel}>{series.label}</span>
            </span>
            <span className={styles.sliceCount}>
                {numberFormatter.format(total)}
            </span>
        </div>
    );
}

/** An empty column's card content on one line: its period, then `message`. */
function DensityChartEmptySlice({
    periodLabel,
    message,
}: {
    periodLabel: string;
    message: string;
}) {
    return (
        <div className={styles.sliceRow}>
            <span className={styles.slicePeriod}>{periodLabel}</span>
            <span className={styles.sliceLabel}>{message}</span>
        </div>
    );
}

interface DensityChartOpenSliceProps extends Omit<
    DensityChartSliceTooltipProps,
    "ariaLabel" | "bucketIndex" | "bucketCount" | "children"
> {
    slice: HoveredSlice;
    buckets: DensityChartBucket[];
    series: DensityChartSeries[];
    dayByDate: ReadonlyMap<string, DensityChartDay>;
    formatPeriod: (period: DensityChartPeriod) => DensityChartPeriodLabel;
    /** What an empty column's card says, e.g. "No activity". */
    emptyMessage: string;
}

/** The open slice's card: a segment's period, series and count, or `emptyMessage` for an empty column. */
export function DensityChartOpenSlice({
    slice,
    buckets,
    series,
    dayByDate,
    formatPeriod,
    emptyMessage,
    ...tooltipProps
}: DensityChartOpenSliceProps) {
    const bucket = buckets[slice.bucketIndex];
    const sliceSeries = series.find(({ id }) => id === slice.seriesId);
    if (!bucket || (!sliceSeries && slice.seriesId !== null)) {
        return null;
    }
    const periodLabel = formatPeriod(bucketToPeriod(bucket)).range;
    let content: ReactNode = (
        <DensityChartEmptySlice
            periodLabel={periodLabel}
            message={emptyMessage}
        />
    );
    if (sliceSeries) {
        const total = bucketSeriesTotal({
            bucket,
            dayByDate,
            seriesId: sliceSeries.id,
        });
        content = (
            <DensityChartSeriesSlice
                series={sliceSeries}
                periodLabel={periodLabel}
                total={total}
            />
        );
    }
    return (
        <DensityChartSliceTooltip
            {...tooltipProps}
            ariaLabel={`${sliceSeries?.label ?? emptyMessage}, ${periodLabel}`}
            bucketIndex={slice.bucketIndex}
            bucketCount={buckets.length}
        >
            {content}
        </DensityChartSliceTooltip>
    );
}
